import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_month: number;
  price_year: number;
  stripe_price_id_month: string;
  stripe_price_id_year: string;
  created_at?: string;
}

interface NewPack {
  name: string;
  credits: number;
  price_month: number;
  price_year: number;
}

const BASE_URL = import.meta.env.VITE_APP_URL || window.location.origin;
const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

export default function CreditPackAdmin() {
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [newPack, setNewPack] = useState<NewPack>({
    name: "",
    credits: 0,
    price_month: 0,
    price_year: 0
  });

  const [editingPackId, setEditingPackId] = useState<string | null>(null);
  const [editingPack, setEditingPack] = useState<CreditPack | null>(null);

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('credit_packs')
        .select('*')
        .order('credits', { ascending: true });

      if (error) {
        console.error('Error fetching packs:', error);
        toast.error('Nepodařilo se načíst balíčky');
        return;
      }

      setPacks(data || []);
    } catch (err) {
      console.error('Error fetching packs:', err);
      toast.error('Nepodařilo se načíst balíčky');
    } finally {
      setLoading(false);
    }
  };

  const createStripeProduct = async (pack: { name: string; credits: number; price: number; description?: string; interval: 'month' | 'year' }) => {
    const response = await fetch(`/api/create-stripe-product`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: pack.name,
        description: pack.description || `${pack.credits} kreditů`,
        credits: pack.credits,
        price: pack.price,
        interval: pack.interval
      })
    });
    if (!response.ok) {
      let errorMsg = 'Nepodařilo se vytvořit Stripe produkt';
      try {
        const errorData = await response.json();
        errorMsg = errorData.error || errorMsg;
      } catch {}
      throw new Error(errorMsg);
    }
    const data = await response.json();
    return { priceId: data.priceId };
  };

  const archiveStripeProduct = async (stripePriceId?: string) => {
    if (!stripePriceId) return;
    try {
      const res = await fetch(`/api/archive-stripe-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: stripePriceId }),
      });
      if (!res.ok) {
        console.warn('Nepodařilo se archivovat starý Stripe produkt');
      }
    } catch (e) {
      console.warn('Chyba při archivaci Stripe produktu:', e);
    }
  };

  const handleChange = (id: string, field: keyof CreditPack, value: string | number) => {
    setPacks(packs.map(pack => pack.id === id ? { ...pack, [field]: value } : pack));
  };

  const handleSave = async (pack: CreditPack) => {
    setSaving(true);
    try {
      await archiveStripeProduct(pack.stripe_price_id_month);
      await archiveStripeProduct(pack.stripe_price_id_year);
      const { priceId: priceIdMonth } = await createStripeProduct({
        name: pack.name,
        credits: pack.credits,
        price: pack.price_month,
        interval: 'month'
      });
      const { priceId: priceIdYear } = await createStripeProduct({
        name: pack.name,
        credits: pack.credits,
        price: pack.price_year,
        interval: 'year'
      });
      pack.stripe_price_id_month = priceIdMonth;
      pack.stripe_price_id_year = priceIdYear;
      const { error } = await supabase.from('credit_packs').update({
        name: pack.name,
        credits: pack.credits,
        price_month: pack.price_month,
        price_year: pack.price_year,
        stripe_price_id_month: pack.stripe_price_id_month,
        stripe_price_id_year: pack.stripe_price_id_year,
      }).eq('id', pack.id);
      if (error) throw error;
      toast.success('Balíček aktualizován');
      fetchPacks();
    } catch (error) {
      console.error('Error saving pack:', error);
      toast.error('Nepodařilo se aktualizovat balíček');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Smazat tento balíček kreditů?')) return;
    const { error } = await supabase.from('credit_packs').delete().eq('id', id);
    if (error) {
      toast.error('Nepodařilo se smazat balíček');
    } else {
      toast.success('Balíček smazán');
      fetchPacks();
    }
  };

  const handleAddPack = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      // Validate input
      if (!newPack.name || newPack.credits <= 0 || newPack.price_month <= 0 || newPack.price_year <= 0) {
        setError('Prosím vyplňte všechna pole správně');
        return;
      }

      // Create monthly Stripe product and price
      const monthlyResponse = await createStripeProduct({
        name: newPack.name,
        credits: newPack.credits,
        price: newPack.price_month,
        interval: 'month'
      });

      // Create yearly Stripe product and price
      const yearlyResponse = await createStripeProduct({
        name: newPack.name,
        credits: newPack.credits,
        price: newPack.price_year,
        interval: 'year'
      });

      // Save to database using service role
      const { data, error: dbError } = await supabase
        .from('credit_packs')
        .insert([
          {
            name: newPack.name,
            credits: newPack.credits,
            price_month: newPack.price_month,
            price_year: newPack.price_year,
            stripe_price_id_month: monthlyResponse.priceId,
            stripe_price_id_year: yearlyResponse.priceId
          }
        ])
        .select()
        .single();

      if (dbError) {
        console.error('Error saving to database:', dbError);
        if (dbError.code === '42501') {
          throw new Error('Nemáte oprávnění pro přidání balíčku. Zkontrolujte, zda jste přihlášen jako admin.');
        } else if (dbError.code === '23505') {
          throw new Error('Balíček s tímto názvem již existuje.');
        } else {
          throw new Error(`Nepodařilo se uložit balíček do databáze: ${dbError.message}`);
        }
      }

      // Update local state
      if (data) {
        setPacks([...packs, data]);
      }
      setNewPack({
        name: "",
        credits: 0,
        price_month: 0,
        price_year: 0
      });
      setSuccess('Balíček byl úspěšně přidán');
      toast.success('Balíček byl úspěšně přidán');
    } catch (err) {
      console.error('Error adding pack:', err);
      const errorMessage = err instanceof Error ? err.message : 'Nepodařilo se přidat balíček';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBuy = async (pack: CreditPack) => {
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: pack.stripe_price_id_month,
          credits: pack.credits,
          successUrl: `${BASE_URL}/credits/success`,
          cancelUrl: `${BASE_URL}/credits/cancel`
        }),
      });

      if (!response.ok) {
        throw new Error('Nepodařilo se vytvořit platební relaci');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error:', error);
      toast.error('Nepodařilo se vytvořit platební relaci');
    }
  };

  const startEdit = (pack: CreditPack) => {
    setEditingPackId(pack.id);
    setEditingPack({ ...pack });
  };

  const cancelEdit = () => {
    setEditingPackId(null);
    setEditingPack(null);
  };

  if (loading) {
    return <div>Načítání...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-4">Správa balíčků kreditů</h2>
        
        {/* Add new pack form */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Přidat nový balíček</h3>
          <form onSubmit={handleAddPack} className="space-y-4">
            <div>
              <Label htmlFor="name">Název balíčku</Label>
              <Input
                id="name"
                value={newPack.name}
                onChange={(e) => setNewPack({ ...newPack, name: e.target.value })}
                placeholder="Např. Základní balíček"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="credits">Počet kreditů</Label>
              <Input
                id="credits"
                type="number"
                value={newPack.credits || ''}
                onChange={(e) => setNewPack({ ...newPack, credits: e.target.value ? parseInt(e.target.value) : 0 })}
                min="1"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="price_month">Měsíční cena (Kč)</Label>
              <Input
                id="price_month"
                type="number"
                value={newPack.price_month || ''}
                onChange={(e) => setNewPack({ ...newPack, price_month: e.target.value ? parseInt(e.target.value) : 0 })}
                min="1"
                required
              />
            </div>

            <div>
              <Label htmlFor="price_year">Roční cena (Kč)</Label>
              <Input
                id="price_year"
                type="number"
                value={newPack.price_year || ''}
                onChange={(e) => setNewPack({ ...newPack, price_year: e.target.value ? parseInt(e.target.value) : 0 })}
                min="1"
                required
              />
              <span className="text-xs text-gray-500">({(newPack.price_year/12).toFixed(2)} Kč/měsíc)</span>
            </div>

            {error && <div className="text-red-500 text-sm">{error}</div>}
            {success && <div className="text-green-500 text-sm">{success}</div>}
            
            <Button type="submit" disabled={saving}>
              {saving ? 'Přidávám...' : 'Přidat balíček'}
            </Button>
          </form>
        </div>
      </div>

      {/* List of existing packs */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Existující balíčky</h3>
        <div className="grid gap-4">
          {packs.map((pack) => (
            <div key={pack.id} className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex justify-between items-start">
                <div>
                  {pack.id === editingPackId ? (
                    <>
                      <Input
                        value={editingPack?.name}
                        onChange={(e) => setEditingPack({ ...editingPack, name: e.target.value })}
                        className="mb-2"
                      />
                      <Input
                        type="number"
                        value={editingPack?.credits}
                        onChange={(e) => setEditingPack({ ...editingPack, credits: parseInt(e.target.value) })}
                        className="mb-2"
                      />
                      <Input
                        type="number"
                        value={editingPack?.price_month}
                        onChange={(e) => setEditingPack({ ...editingPack, price_month: parseInt(e.target.value) })}
                        className="mb-2"
                      />
                      <Input
                        type="number"
                        value={editingPack?.price_year}
                        onChange={(e) => setEditingPack({ ...editingPack, price_year: parseInt(e.target.value) })}
                        className="mb-2"
                      />
                    </>
                  ) : (
                    <>
                      <h4 className="font-medium">{pack.name}</h4>
                      <p className="text-sm text-gray-500">{pack.credits} kreditů</p>
                    </>
                  )}
                </div>
                <div className="text-right">
                  {pack.id === editingPackId ? (
                    <>
                      <Button size="sm" className="mr-2" onClick={() => handleSave(editingPack)}>Uložit</Button>
                      <Button size="sm" variant="secondary" onClick={cancelEdit}>Zrušit</Button>
                    </>
                  ) : (
                    <>
                      <p className="font-medium">{pack.price_month} Kč/měsíc</p>
                      <p className="text-sm text-gray-500">{pack.price_year} Kč/rok</p>
                      <Button size="sm" className="mr-2 mt-2" onClick={() => startEdit(pack)}>Upravit</Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(pack.id)}>Smazat</Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 